import React, { useState, useEffect, useRef } from 'react';

import Titulo from '@components/Titulo';
import { verCitasApi } from "@api/wp/agenda/citas/citasApi";
import { cancelarCitaPromoCostaApi, cancelarCitaPromoNorteApi, cancelarCitaCalidadMedicaApi } from "@api/wp/agenda/cancelar/cancelarApi";
import { cancelarCitaLocalApi } from "@api/wp/agenda/cancelar/cancelarApi";
import Boton from '@components/Boton';
import Advertencia from '@components/Advertencia';
import BotonBold from '@components/BotonBold';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PaymentIcon from '@mui/icons-material/Payment';

import { viewEmpresasApi } from "@api/wp/empresas/empresasApi";
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorAdminStyle } from '@styles/cajaContenedorAdmin';
import { colors } from '@styles/colors';
import '@styles/modernTable.css';
import LoadingOverlay from '@components/LoadingOverlay';
import LabelCarga from '@components/LabelCarga';
import { LAYOUT_COMPACT_MAX_PX } from '@utils/breakpoints';
import { citaHoraToHms } from '@utils/citaHoraParse';
import { DataCard, DataCardList } from '@components/DataCard';

const MS_UNA_HORA = 60 * 60 * 1000;

function citaFechaToYmd(fecha) {
  if (!fecha) return null;
  if (typeof fecha === 'string') {
    const m = fecha.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
  }
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    const y = fecha.getFullYear();
    const mo = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${mo}-${d}`;
  }
  return null;
}

/** Inicio de la cita en hora local (ms), o null si no se puede interpretar. */
function getInicioCitaMs(fecha, hora) {
  const ymd = citaFechaToYmd(fecha);
  if (!ymd) return null;
  const hms = citaHoraToHms(hora);
  const d = new Date(`${ymd}T${hms}`);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function reglasCancelacionPorPlazo(cita, ahoraMs = Date.now()) {
  const inicioMs = getInicioCitaMs(cita?.fecha, cita?.hora);
  if (inicioMs == null) {
    return {
      permitido: false,
      motivo: 'No hay fecha u hora de cita válida para cancelar.',
    };
  }
  const delta = inicioMs - ahoraMs;
  if (delta <= 0) {
    return {
      permitido: false,
      motivo: 'No se puede cancelar: la fecha y hora de la cita ya pasaron.',
    };
  }
  if (delta < MS_UNA_HORA) {
    return {
      permitido: false,
      motivo: 'No se puede cancelar: faltan menos de 1 hora para la cita.',
    };
  }
  return { permitido: true, motivo: '' };
}

function citaEstaCancelada(cita) {
  return String(cita?.estado ?? '').trim().toLowerCase() === 'cancelada';
}

function citaMetodoEsBold(cita) {
  return String(cita?.metodo ?? '').trim().toLowerCase() === 'bold';
}

/** Pago Bold confirmado vía oficina_bold_response (idResBold → type). */
function boldVentaAprobada(cita) {
  return citaMetodoEsBold(cita) && String(cita?.bold_response_type ?? '').trim() === 'SALE_APPROVED';
}

function Citas() {
  const [error, setError] = useState('');
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalCancelarVisible, setModalCancelarVisible] = useState(false);
  const [modalErrorVisible, setModalErrorVisible] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [pasoCancelacion, setPasoCancelacion] = useState(1);
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= LAYOUT_COMPACT_MAX_PX
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${LAYOUT_COMPACT_MAX_PX}px)`);
    const apply = () => setIsCompact(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const [, setRelojPlazoCancelar] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setRelojPlazoCancelar((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Función auxiliar para generar Hash (copiada de confirmar.jsx)
  async function generateHash(cadena) {
    const encodedText = new TextEncoder().encode(cadena);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedText);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  // Componente interno para fila de cita que maneja su propio estado de Bold
  const FilaCita = ({ cita, abrirModalCancelacion, citaSeleccionada, compact }) => {
    const [integritySignature, setIntegritySignature] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [loadingBold, setLoadingBold] = useState(false);
    const boldBtnRef = useRef(null);
    const modoRevBold = 0; // 0: Prod, 1: Dev - Ajustar según entorno global o prop

    const iniciarPago = async () => {
        try {
            if (!cita.order_id || cita.order_id === '-' || cita.order_id === '0') {
                alert("Esta cita no tiene un Order ID válido.");
                return;
            }
            setLoadingBold(true);

            // 1. Obtener credenciales de la empresa
            const empresaNombre = cita.empresa || cita.nombre_empresa || "";
            let currentKey = "";
            let currentClave = "";

            const resp = await viewEmpresasApi({ nombre_empresa: empresaNombre });
            const lista = resp?.result || [];
            if (lista.length > 0) {
                if (modoRevBold === 0) {
                    currentKey = lista[0]?.key_bold_prod;
                    currentClave = lista[0]?.clave_bold_prod;
                } else {
                    currentKey = lista[0]?.key_bold_dev;
                    currentClave = lista[0]?.clave_bold_dev;
                }
            }

            if (!currentKey || !currentClave) {
                alert("No se encontraron credenciales de pago para la empresa asignada.");
                setLoadingBold(false);
                return;
            }
            setApiKey(currentKey);

            // 2. Generar Hash
            const amount = String(Number(cita.valor || 0));
            const currency = 'COP';
            const orderId = cita.order_id;
            const cadenaConcatenada = `${orderId}${amount}${currency}${currentClave}`;
            const hash = await generateHash(cadenaConcatenada);
            setIntegritySignature(hash);

            // 3. Abrir Checkout (esperar a que el estado se actualice y luego abrir)
            setTimeout(() => {
                if (boldBtnRef.current && boldBtnRef.current.openCheckout) {
                    boldBtnRef.current.openCheckout();
                }
                setLoadingBold(false);
            }, 100);

        } catch (error) {
            console.error("Error iniciando pago Bold:", error);
            setLoadingBold(false);
        }
    };
    const metodo = cita?.metodo;
    const metodoVentanilla = String(metodo ?? '').trim().toLowerCase() === 'ventanilla';
    const puedePagar = Number(cita?.CitaID ?? 0) > 0;
    const cancelada = citaEstaCancelada(cita);
    const orderOk =
      cita?.order_id &&
      String(cita.order_id).trim() !== '' &&
      cita.order_id !== '-' &&
      cita.order_id !== '0';
    const pagoLineaOkNoBold = !citaMetodoEsBold(cita) && Number(cita?.idResBold ?? 0) > 0;
    const formatFecha = (s) => {
      if (!s) return '';
      const parts = String(s).split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        return `${d}/${m}/${y}`;
      }
      return s;
    };

    const sinFecha = !cita.fecha;
    const sinHora = !cita.hora || String(cita.hora).trim() === '';
    const estiloRecuadroFechaHora = (placeholder, esHora = false) => ({
      display: 'inline-block',
      fontWeight: placeholder ? 600 : 700,
      fontFamily: 'Inter, sans-serif',
      backgroundColor: placeholder ? colors.gray200 : '#FFF3E8',
      color: placeholder ? colors.gray600 : '#9E4410',
      border: `1px solid ${placeholder ? colors.borderLight : '#F0C9A8'}`,
      borderRadius: compact ? 12 : 10,
      padding: compact ? '8px 14px' : '0.55vh 1.25vh',
      fontSize: esHora
        ? compact
          ? 'clamp(11px, 2.8vw, 14px)'
          : 'clamp(10px, 1.1vh, 12px)'
        : compact
          ? 'clamp(14px, 3.6vw, 17px)'
          : 'clamp(13px, 1.4vh, 15px)',
      lineHeight: 1.25,
      boxShadow: placeholder ? 'none' : '0 1px 4px rgba(180, 95, 35, 0.12)',
      letterSpacing: '0.02em',
    });
    const valorFechaDestacado = (
      <span style={estiloRecuadroFechaHora(sinFecha)}>
        {sinFecha ? 'No especificada' : formatFecha(cita.fecha)}
      </span>
    );
    const valorHoraDestacada = (
      <span style={estiloRecuadroFechaHora(sinHora, true)}>
        {sinHora ? 'No especificada' : String(cita.hora).trim()}
      </span>
    );

    const iconSz = compact ? '3.2vh' : '4.0vh';

    const renderCeldaPago = () => {
      const aprobadoBold = boldVentaAprobada(cita);
      if (aprobadoBold || pagoLineaOkNoBold) {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh', flexWrap: 'wrap' }} title="Pago en linea">
            <CreditCardIcon style={{ color: colors.success, fontSize: iconSz }} />
            {aprobadoBold && (
              <>
                <span style={{ fontWeight: 700, color: colors.success, fontSize: compact ? 'clamp(13px, 3.2vw, 15px)' : '1.35vh', fontFamily: 'Inter, sans-serif' }}>
                  PAGADA
                </span>
              </>
            )}
          </span>
        );
      }
      const mostrarRealizarPago =
        !metodoVentanilla &&
        !cancelada &&
        puedePagar &&
        (!citaMetodoEsBold(cita) || orderOk);
      if (mostrarRealizarPago) {
        return (
          <>
            <div style={{ display: 'none' }}>
              <BotonBold
                ref={boldBtnRef}
                integritySignature={integritySignature}
                onClick={() => { }}
                apiKey={apiKey}
                orderId={cita.order_id}
                amount={String(Number(cita.valor || 0))}
                description={cita.nombre_espe || 'Pago Cita Médica'}
              />
            </div>
            <div
              onClick={loadingBold ? undefined : iniciarPago}
              style={{
                cursor: loadingBold ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5vh',
                backgroundColor: colors.verdeClaro,
                padding: compact ? '0.8vh 1.4vh' : '0.5vh 1vh',
                borderRadius: '1vh',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              title="Pagar con Bold"
            >
              {loadingBold ? (
                <span style={{ fontSize: '1.2vh' }}>Cargando...</span>
              ) : (
                <>
                  <span style={{ fontSize: '1.4vh', fontWeight: 600 }}>Realizar pago</span>
                  <PaymentIcon sx={{ fontSize: '2.5vh', color: colors.primaryText }} />
                </>
              )}
            </div>
          </>
        );
      }
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh' }} title="Pago en ventanilla">
          <StorefrontIcon style={{ color: colors.gray600, fontSize: iconSz }} />
        </span>
      );
    };

    const { permitido: puedeCancelar, motivo: motivoNoCancelar } = reglasCancelacionPorPlazo(cita);

    const renderCeldaCancelar = () => {
      if (String(cita?.estado).toLowerCase() === 'cancelada') {
        return (
          <span style={{
            display: 'inline-block',
            padding: '0.6vh 1.2vh',
            borderRadius: '5vh',
            backgroundColor: colors.gray200,
            color: colors.gray700,
            fontFamily: 'Inter',
            fontSize: '1.3vh',
            fontWeight: 600,
          }}
          >
            Cancelada
          </span>
        );
      }
      const bloqueadoPorPlazo = !puedeCancelar;
      const bloqueadoPorSeleccion = citaSeleccionada?.id === cita.id;
      const deshabilitado = bloqueadoPorPlazo || bloqueadoPorSeleccion;
      return (
        <button
          type="button"
          onClick={() => abrirModalCancelacion(cita)}
          disabled={deshabilitado}
          title={bloqueadoPorPlazo ? motivoNoCancelar : 'Cancelar cita'}
          style={{
            backgroundColor: deshabilitado ? '#ccc' : colors.cancelar,
            color: colors.black,
            border: 'none',
            borderRadius: '5vh',
            padding: '0.6vh 1.2vh',
            cursor: deshabilitado ? 'not-allowed' : 'pointer',
            fontFamily: 'Inter',
            fontSize: '1.3vh',
            opacity: bloqueadoPorPlazo ? 0.75 : 1,
          }}
        >
          Cancelar
        </button>
      );
    };

    if (compact) {
      return (
        <DataCard
          rows={[
            { label: 'ID Cita', value: <span style={{ color: '#007bff', fontWeight: '500' }}>{cita.CitaID ?? 'N/A'}</span> },
            { label: 'Servicio', value: cita.nombre_espe || 'No especificado' },
            { label: 'Sede', value: cita.nombre_sede || 'No especificada' },
            { label: 'Médico', value: cita.nombreMedico || 'No especificado' },
            { label: 'Fecha', value: valorFechaDestacado },
            { label: 'Hora', value: valorHoraDestacada },
            {
              label: (
                <span style={{ whiteSpace: 'nowrap' }}>Estado Cita</span>
              ),
              value: (
                <span style={{ fontWeight: 600 }}>
                  {cita.estado === 'pagado' ? '' : 'ASIGNADA'}
                </span>
              ),
            },
            { label: 'Estado pago', value: renderCeldaPago(), layout: 'stack' },
            { label: 'Cancelar', value: renderCeldaCancelar(), layout: 'stack' },
          ]}
        />
      );
    }

    return (
      <tr>
        <td style={{ color: '#007bff', fontWeight: '500' }}>{cita.CitaID ?? 'N/A'}</td>
        <td>{cita.nombre_espe || 'No especificado'}</td>
        <td>{cita.nombre_sede || 'No especificada'}</td>
        <td>{cita.nombreMedico || 'No especificado'}</td>
        <td>{valorFechaDestacado}</td>
        <td>{valorHoraDestacada}</td>
        <td>
          {cita.estado === 'pagado' ? '' : 'ASIGNADA'}
        </td>
        <td style={{ textAlign: 'center' }}>{renderCeldaPago()}</td>
        <td>{renderCeldaCancelar()}</td>
      </tr>
    );
  };

  const abrirModalCancelacion = (cita) => {
    const { permitido, motivo } = reglasCancelacionPorPlazo(cita);
    if (!permitido) {
      setMensajeError(motivo);
      setModalErrorVisible(true);
      return;
    }
    setCitaSeleccionada(cita);
    setMotivoCancelacion('');
    setPasoCancelacion(1);
    setModalCancelarVisible(true);
  };

  const confirmarCancelacion = async () => {
    if (!citaSeleccionada) return;
    const { permitido, motivo } = reglasCancelacionPorPlazo(citaSeleccionada);
    if (!permitido) {
      setModalCancelarVisible(false);
      setCitaSeleccionada(null);
      setMensajeError(motivo);
      setModalErrorVisible(true);
      return;
    }
    if (!motivoCancelacion) {
      alert('Por favor, seleccione un motivo de cancelación');
      return;
    }
    setCancelando(true);
    try {
      const empresaRaw = (citaSeleccionada?.empresa || citaSeleccionada?.nombre_empresa || citaSeleccionada?.empresaNombre || '').toString();
      const empresaSlug = empresaRaw.trim().toLowerCase().replace(/\s+/g, '-');
      const payload = {
        citaMedicaOid: citaSeleccionada?.CitaID ?? 0,
        causaCancelacionOid: parseInt(motivoCancelacion, 10),
        observacion: 'Cancelada por usuario'
      };
console.log(payload)
      let apiFn = null;
      if (empresaSlug === 'promocosta') {
        apiFn = cancelarCitaPromoCostaApi;
      } else if (empresaSlug === 'promonorte' || empresaSlug === 'salud-social') {
        apiFn = cancelarCitaPromoNorteApi;
      } else if (empresaSlug === 'calidad-medica') {
        apiFn = cancelarCitaCalidadMedicaApi;
      } else {
        // Fallback razonable
        apiFn = cancelarCitaPromoNorteApi;
      }

      const response = await apiFn(payload);
      console.log('response', response)
      
      const exitoRemoto = Boolean(
        (response && (response.code === 200 || response.statusCode === 200)) ||
        (response?.CitaMedicaOid && typeof response?.Mensaje === 'string' && /cancelad/i.test(response.Mensaje))
      );

      if (exitoRemoto) {
        try {
          const localPayload = {
            id: citaSeleccionada?.id ?? 0,
            CitaID: citaSeleccionada?.CitaID ?? response?.CitaMedicaOid ?? 0,
            estado: 'Cancelada',
            motivoCancel: String(motivoCancelacion)
          };
          console.log('REQ  ',localPayload )
          const localResp = await cancelarCitaLocalApi(localPayload);
          console.log('RESPONSE ',localResp )
          const okLocal = localResp?.statusCode === 200 || localResp?.code === 200;
          if (!okLocal) {
            setModalCancelarVisible(false);
            setMensajeError('No se pudo actualizar el estado local de la cita. Intente de nuevo más tarde.');
            setModalErrorVisible(true);
            setCitaSeleccionada(null);
            return;
          }
        } catch (e) {
          console.error('Error actualizando estado local de la cita:', e);
          setModalCancelarVisible(false);
          setMensajeError('Ocurrió un error al actualizar el estado local de la cita.');
          setModalErrorVisible(true);
          setCitaSeleccionada(null);
          return;
        }
        setCitas(prev => prev.map(x => {
          if (x.id === (citaSeleccionada?.id)) {
            return { ...x, estado: 'Cancelada', motivoCancel: String(motivoCancelacion) };
          }
          return x;
        }));
        setModalCancelarVisible(false);
        setCitaSeleccionada(null);
      } else {
        setModalCancelarVisible(false);
        setMensajeError('No se pudo cancelar la cita. Intente de nuevo más tarde.');
        setModalErrorVisible(true);
        setCitaSeleccionada(null);
      }
    } catch (e) {
      console.error('Error al cancelar cita:', e);
      setModalCancelarVisible(false);
      // Extraer mensaje de error de la API si existe
      let errorMessage = 'Ocurrió un error cancelando la cita. Por favor, intente de nuevo.';
      if (e && typeof e === 'object' && e.mensaje) {
        errorMessage = e.mensaje;
      } else if (e && typeof e === 'object' && e.message) {
        errorMessage = e.message;
      }
      setMensajeError(errorMessage);
      setModalErrorVisible(true);
      setCitaSeleccionada(null);
    } finally {
      setCancelando(false);
    }
  };
  // Función para cargar las citas desde la API
  const cargarCitas = async () => {
    try {
      setCargando(true);
      const idPaciente = localStorage.getItem('idUsu');
      const response = await verCitasApi({ id_paciente: idPaciente });
      console.log('response Citas ', response)
      if (response.statusCode === 200 && response.result) {
        const result = Array.isArray(response.result) ? response.result : [response.result];
        const completas = result.map((c) => ({
          ...c,
          nombre_espe: c.nombre_espe || c.nombre_espe_db || '',
        }));
        setCitas(completas);
      } else {
        setError('No se pudieron cargar las citas');
      }
    } catch (error) {
      console.error('Error al cargar citas:', error);
      setError('Error al cargar las citas');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarCitas();
  }, []);

  return (
    <div id="cajaPrincipal"
      style={cajaPrincipalStyle}
    >
      <LoadingOverlay visible={cargando} />      
      <Titulo titulo="Historial de citas" subtitulo=""></Titulo>
      {/* Contenido del primer div */}
      <div
        style={{
          ...cajaContenedorAdminStyle,
          ...(isCompact && {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 'clamp(8px, 2vw, 18px)',
            paddingRight: 'clamp(8px, 2vw, 18px)',
            boxSizing: 'border-box',
          }),
        }}
      >
        {/* Tabla escritorio: mismo criterio que administración (modernTable.css) */}

        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            marginTop: '1vh',
          }}
        >
          {cargando ? (
            <LabelCarga nombre="citas" />
          ) : (Array.isArray(citas) && citas.length > 0 ? (
            isCompact ? (
              <DataCardList>
                {citas.map((cita) => (
                  <FilaCita
                    key={cita.id}
                    cita={cita}
                    abrirModalCancelacion={abrirModalCancelacion}
                    citaSeleccionada={citaSeleccionada}
                    compact
                  />
                ))}
              </DataCardList>
            ) : (
              <div className="modern-table-container">
                <table id="tabla-citas" className="modern-table">
                  <thead>
                    <tr>
                      <th>ID Cita</th>
                      <th>Servicio</th>
                      <th>Sede</th>
                      <th>Médico</th>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Estado&nbsp;Cita</th>
                      <th>Estado Pago</th>
                      <th>Cancelar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {citas.map((cita) => (
                      <FilaCita
                        key={cita.id}
                        cita={cita}
                        abrirModalCancelacion={abrirModalCancelacion}
                        citaSeleccionada={citaSeleccionada}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              fontSize: '1.4vh',
              fontFamily: 'Inter',
              color: colors.secondaryButton
            }}>
              No hay citas disponibles
            </div>
          ))}
        </div>
      </div>

      {/* Modal de confirmación de cancelación */}
      {modalCancelarVisible && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '2vh', padding: '4vh', maxWidth: '80vh', width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)', textAlign: 'center', fontFamily: 'Inter'
          }}>
            {pasoCancelacion === 1 ? (
              <>
                <h2 style={{ color: colors.primaryText, fontSize: '2.2vh', fontWeight: 'bold', margin: 0 }}>
                  ¿Desea cancelar esta cita?
                </h2>
                <div style={{ display: 'flex', gap: '2vh', justifyContent: 'center', marginTop: '4vh' }}>
                  <Boton
                    label="Sí, cancelar"
                    onClick={() => setPasoCancelacion(2)}
                  />
                  <Boton
                    label="No, volver"
                    color={colors.gray600}
                    onClick={() => { setModalCancelarVisible(false); setCitaSeleccionada(null); }}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 style={{ color: colors.primaryText, fontSize: '2.2vh', fontWeight: 'bold', margin: 0 }}>
                  Por favor, seleccione el motivo de cancelación
                </h2>
                
                <div style={{ marginTop: '3vh', textAlign: 'left' }}>
                  <label style={{ display: 'block', marginBottom: '1vh', fontSize: '1.6vh', color: colors.primaryText, fontWeight: '500' }}>
                    Motivo:
                  </label>
                  <select
                    value={motivoCancelacion}
                    onChange={(e) => setMotivoCancelacion(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '1.5vh',
                      borderRadius: '1vh',
                      border: `1px solid ${colors.gray300 || '#ccc'}`,
                      fontSize: '1.5vh',
                      fontFamily: 'Inter',
                      outline: 'none',
                      backgroundColor: '#f9f9f9',
                      color: colors.primaryText
                    }}
                  >
                    <option value="" disabled>Seleccione un motivo...</option>
                    <option value="1">Mi estado de salud cambió o ya no necesito la atención</option>
                    <option value="2">Tengo un compromiso personal o laboral que coincide con la cita</option>
                    <option value="3">No puedo desplazarme al lugar de atención</option>
                    <option value="4">Decidí atenderme en otra institución o con otro profesional</option>
                    <option value="5">No cuento con el dinero para el pago correspondiente</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '2vh', justifyContent: 'center', marginTop: '4vh' }}>
                  <Boton
                    label={cancelando ? 'Cancelando...' : 'Confirmar'}
                    onClick={cancelando ? undefined : confirmarCancelacion}
                    disabled={!motivoCancelacion || cancelando}
                  />
                  <Boton
                    label="Volver"
                    color={colors.gray600}
                    onClick={() => setPasoCancelacion(1)}
                    disabled={cancelando}
                  />
                </div>
              </>
            )}
         </div>
        </div>
      )}

      {/* Modal de error */}
      {modalErrorVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '2vh',
            padding: '4vh',
            maxWidth: '80vh',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Advertencia mensaje={mensajeError || "Ocurrió un error inesperado"} ancho="100%" />

            {/* Botones */}
            <div style={{
              display: 'flex',
              gap: '2vh',
              justifyContent: 'center',
              marginTop: '3vh'
            }}>
              <Boton
                label="Aceptar"
                onClick={() => {
                  setModalErrorVisible(false);
                }}
                ancho="20vh"
                alto="5vh"
              />
            </div>            
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div style={{
          marginTop: '2vh',
          marginLeft: '6vh',
          color: 'red',
          fontSize: '1.6vh',
          fontFamily: 'Inter'
        }}>
          {error}
        </div>
      )}

    </div>
  );
}
export default Citas;
